import type { ChangeEvent } from 'react'
import { useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/dialog'
import { activateOnKey } from '@/lib/keyboard-activation'
import { cn } from '@/lib/utils'
import {
  CameraIcon,
  CircleNotchIcon,
  TrashIcon,
  UploadSimpleIcon,
} from '@phosphor-icons/react'
import Cropper, { ReactCropperElement } from 'react-cropper'

import { ModuleTypeEnum, PermissionActionEnum } from '@scrambl/shared'

import { ModuleAvatar } from './module-avatar'

import 'cropperjs/dist/cropper.css'

import { usePermissions } from '@/hooks/usePermissions'
import { getMutationErrorMessage } from '@/lib/api/mutation-feedback'
import { createLogger } from '@/lib/logger'
import { PhotoService } from '@/lib/services/photo.service'
import { toast } from 'sonner'

const logger = createLogger('components/ui/editable-module-avatar')

interface EditableModuleAvatarProps {
  entity: {
    id?: string
    name?: string
    code?: string
    logoUrl?: string
  } | null
  moduleType: ModuleTypeEnum
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl'
  className?: string
  showFallbackIcon?: boolean
  /** If true, avatar will check permissions and allow editing if user has MANAGE permission */
  editable?: boolean
  onLogoChange?: (file: File | null) => Promise<void>
  onClick?: () => void
}

/**
 * EditableModuleAvatar - A ModuleAvatar with optional logo upload/edit functionality
 *
 * When editable is true, clicking the avatar opens a dialog to upload a new logo.
 * Supports image cropping before upload.
 */
export function EditableModuleAvatar({
  entity,
  moduleType,
  size = 'md',
  className,
  showFallbackIcon = true,
  editable = false,
  onLogoChange,
  onClick,
}: EditableModuleAvatarProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [cropImage, setCropImage] = useState<string | null>(null)
  const [localPreviewUrl, setLocalPreviewUrl] = useState<string | null>(null)
  const cropperRef = useRef<ReactCropperElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Check if user has MANAGE permission for this module type
  const { checkPermission } = usePermissions()
  const canEdit =
    editable &&
    onLogoChange &&
    checkPermission(moduleType, PermissionActionEnum.MANAGE)

  const openFilePicker = () => {
    if (isProcessing) return
    fileInputRef.current?.click()
  }

  const handleAvatarClick = () => {
    if (canEdit) {
      setIsDialogOpen(true)
    } else if (onClick) {
      onClick()
    }
  }

  const handleFileSelect = async (file: File) => {
    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB')
      return
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Select an image file')
      return
    }

    try {
      setIsProcessing(true)
      const processed = await PhotoService.processPhoto(file, {
        maxWidth: 800,
        maxHeight: 800,
        quality: 0.85,
      })
      setCropImage(processed.url)
    } catch (e) {
      logger.error('Error processing image:', e)
      toast.error("Couldn't read that image. Try another file.")
    } finally {
      setIsProcessing(false)
    }
  }

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      void handleFileSelect(file)
    }
  }

  const handleSaveLogo = async () => {
    if (!cropperRef.current || !onLogoChange) return

    try {
      setIsProcessing(true)
      const croppedCanvas = PhotoService.getCroppedCanvas(
        cropperRef.current.cropper,
        {
          maxWidth: 800,
          maxHeight: 800,
        },
      )
      const blob = await PhotoService.canvasToBlob(croppedCanvas, 0.9)
      const file = new File([blob], `logo-${Date.now()}.jpg`, {
        type: 'image/jpeg',
        lastModified: Date.now(),
      })

      // Create preview URL
      const preview = URL.createObjectURL(file)
      if (localPreviewUrl) URL.revokeObjectURL(localPreviewUrl)
      setLocalPreviewUrl(preview)

      await onLogoChange(file)

      // Cleanup
      if (cropImage) PhotoService.revokeObjectUrl(cropImage)
      setCropImage(null)
      setIsDialogOpen(false)
      toast.success('Logo updated')
    } catch (err) {
      logger.error('Error saving logo:', err)
      toast.error(getMutationErrorMessage(err, "Couldn't save the logo."))
    } finally {
      setIsProcessing(false)
    }
  }

  const handleRemoveLogo = async () => {
    if (!onLogoChange) return

    try {
      setIsProcessing(true)
      await onLogoChange(null)
      if (localPreviewUrl) {
        URL.revokeObjectURL(localPreviewUrl)
        setLocalPreviewUrl(null)
      }
      setIsDialogOpen(false)
      toast.success('Logo removed')
    } catch (err) {
      logger.error('Error removing logo:', err)
      toast.error(getMutationErrorMessage(err, "Couldn't remove the logo."))
    } finally {
      setIsProcessing(false)
    }
  }

  const closeDialog = () => {
    if (cropImage) PhotoService.revokeObjectUrl(cropImage)
    setCropImage(null)
    setIsDialogOpen(false)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const hasLogo = !!(entity?.logoUrl || localPreviewUrl)

  return (
    <>
      <div className="group relative">
        <ModuleAvatar
          entity={entity}
          moduleType={moduleType}
          logoUrl={localPreviewUrl || entity?.logoUrl}
          size={size}
          className={cn(canEdit && 'cursor-pointer', className)}
          showFallbackIcon={showFallbackIcon}
          onClick={handleAvatarClick}
        />

        {/* Edit overlay - only shown if user has MANAGE permission */}
        {canEdit && (
          <button
            type="button"
            aria-label={hasLogo ? 'Change logo' : 'Upload logo'}
            className={cn(
              'absolute inset-0 flex cursor-pointer items-center justify-center bg-black/50 opacity-0 transition-opacity focus-visible:opacity-100 group-hover:opacity-100',
              size === '2xl' ? 'rounded-lg' : 'rounded-lg',
              // Match avatar shape classes
              [ModuleTypeEnum.MEMBER, ModuleTypeEnum.CONTACT].includes(
                moduleType,
              )
                ? 'rounded-full'
                : 'rounded-lg',
            )}
            onClick={handleAvatarClick}
          >
            <CameraIcon
              className={cn(
                'text-white',
                size === 'xs'
                  ? 'h-3 w-3'
                  : size === 'sm'
                    ? 'h-4 w-4'
                    : size === 'md'
                      ? 'h-5 w-5'
                      : size === 'lg'
                        ? 'h-6 w-6'
                        : size === 'xl'
                          ? 'h-8 w-8'
                          : 'h-12 w-12',
              )}
            />
          </button>
        )}
      </div>

      {/* Upload Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={open => !open && closeDialog()}>
        <DialogContent className="overflow-hidden rounded-xl border-0 bg-background p-0 shadow-lg sm:max-w-[500px]">
          <DialogTitle className="sr-only">Update logo</DialogTitle>
          <DialogDescription className="sr-only">
            Upload a new logo image
          </DialogDescription>

          <div className="mb-5 flex justify-center pt-8">
            <div className="relative flex h-16 w-16 items-center justify-center overflow-hidden rounded-2xl bg-primary/5 shadow-sm">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-transparent"></div>
              <CameraIcon className="h-7 w-7 text-primary" />
            </div>
          </div>

          <div className="mb-4 px-6 text-center">
            <h3 className="text-lg font-medium">
              {cropImage ? 'Adjust logo' : 'Update logo'}
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {cropImage
                ? 'Drag and resize to set a square logo'
                : 'Upload a new logo image'}
            </p>
          </div>

          <div className="px-6 pb-4">
            {cropImage ? (
              <div className="overflow-hidden rounded-xl border border-border/30 bg-card/50 p-1">
                <Cropper
                  ref={cropperRef}
                  src={cropImage}
                  style={{ height: 350, width: '100%' }}
                  aspectRatio={1}
                  guides={true}
                  viewMode={1}
                  dragMode="move"
                  background={false}
                  responsive={true}
                  autoCropArea={1}
                  checkOrientation={false}
                  cropBoxMovable={true}
                  cropBoxResizable={true}
                  toggleDragModeOnDblclick={false}
                />
              </div>
            ) : (
              <div
                className={cn(
                  'cursor-pointer rounded-xl border-2 border-dashed p-8 text-center transition-colors hover:border-primary/50',
                  isProcessing && 'pointer-events-none opacity-50',
                )}
                role="button"
                tabIndex={isProcessing ? -1 : 0}
                aria-disabled={isProcessing || undefined}
                onClick={openFilePicker}
                onKeyDown={activateOnKey(openFilePicker)}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleInputChange}
                  className="hidden"
                  disabled={isProcessing}
                />
                {isProcessing ? (
                  <CircleNotchIcon className="mx-auto mb-3 h-8 w-8 animate-spin text-muted-foreground" />
                ) : (
                  <UploadSimpleIcon className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
                )}
                <p className="text-sm text-muted-foreground">
                  {isProcessing
                    ? 'Processing…'
                    : 'Click to upload or drag image here'}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  PNG, JPG up to 5MB
                </p>
              </div>
            )}
          </div>

          <DialogFooter className="flex justify-between px-6 pb-6">
            <div>
              {hasLogo && !cropImage && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleRemoveLogo}
                  disabled={isProcessing}
                  className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                >
                  <TrashIcon className="mr-2 h-4 w-4" />
                  Remove
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={closeDialog}
                disabled={isProcessing}
                className="h-9 rounded-full px-4"
              >
                Cancel
              </Button>
              {cropImage && (
                <Button
                  type="button"
                  onClick={handleSaveLogo}
                  disabled={isProcessing}
                  className="h-9 rounded-full px-5"
                >
                  {isProcessing ? (
                    <>
                      <CircleNotchIcon className="mr-2 h-4 w-4 animate-spin" />
                      Saving…
                    </>
                  ) : (
                    'Save logo'
                  )}
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
